import React from 'react'
import { SEO } from 'newComponents'
import Layout from '../components/layout'
import ClosingCTAs from '../components/ClosingCTAs'
import CtaArrow from '../components/ctaArrow'
import '../styles/security.scss'

import soc2 from '../images/security/Soc2TypeII.svg'
import iso from '../images/security/isoImage.png'
import gdpr from '../images/security/GDPR.jpg'
import privacy from '../images/security/privacy_shield.png'
import analytics from '../images/security/StaticAnalytics_blue.svg'
import encrypt from '../images/illustrations/small/EndtoEndEncryption.svg'
import penTest from '../images/illustrations/small/PenetrationTest.svg'
import mfa from '../images/illustrations/small/mfa.svg'
import sso from '../images/illustrations/small/sso-saml.svg'
import auditLog from '../images/illustrations/small/audit-log.svg'
import custom from '../images/illustrations/small/custom-roles.svg'

const Security = () => (
  <Layout>
    <SEO
      title="Security"
      description="Security is our top priority. We're dedicated to ensuring our customers can trust our platform to manage their software features. Learn about trust and security at LaunchDarkly."
    />
    <section className="section is-large bg-image security">
      <div className="container content hero">
        <div className="columns is-centered">
          <div className="column has-text-centered-tablet is-8">
            <h1 className="title">Trust & Security at LaunchDarkly</h1>
            <p className="text-large">
              Security is very important to us. We are proud to exceed the industry standard when it comes to protecting
              your organization.
            </p>
          </div>
        </div>
      </div>
    </section>
    <section className="security-covid-banner has-text-centered-desktop">
      <p>
        Questions about LaunchDarkly’s services during the COVID pandemic?{' '}
        <a className="cta u-nowrap" href="/downloads/COVID19_Business_Continutity_Planning.pdf">
          Learn More
          <CtaArrow />
        </a>
      </p>
    </section>
    <section className="section">
      <div className="container content">
        <div className="section-title">
          <h2 className="title">Compliance Certifications & Regulations</h2>
          <p className="subtitle">
            These third-party assurances show how we've implemented security best practices on your behalf.
          </p>
        </div>
        <div className="columns is-centered">
          <div className="column has-text-centered-tablet is-3">
            <div className="large-icon">
              <img src={soc2} alt="soc2" />
            </div>
            <h5>SOC 2 Type II</h5>
            <p>Trust Services Principles</p>
          </div>
          <div className="column has-text-centered-tablet is-3">
            <div className="large-icon">
              <img src={iso} alt="iso" />
            </div>
            <h5>ISO 27001</h5>
            <p>Information Security Management System (ISMS)</p>
          </div>
          <div className="column has-text-centered-tablet is-3">
            <div className="large-icon">
              <img src={gdpr} alt="gdpr" />
            </div>
            <h5>GDPR</h5>
            <p>EU General Data Protection Regulation</p>
          </div>
          <div className="column has-text-centered-tablet is-3">
            <div className="large-icon">
              <img src={privacy} alt="privacy" />
            </div>
            <h5>EU/US Privacy Shield</h5>
            <p>Data Privacy Practices</p>
          </div>
        </div>
      </div>
    </section>
    <section className="section is-large bg-dark bg-static-stars">
      <div className="container content">
        <div className="columns is-mobile is-multiline">
          <div className="column is-8-desktop is-12-mobile">
            <h2 className="title">User Data</h2>
            <p>
              LaunchDarkly does not require any personally identifiable information (PII) be passed to the service. With
              our enterprise plans, teams can still collect event data from feature flag evaluations while controlling
              which user attribute fields are stored or exposed within LaunchDarkly.
            </p>
            <a
              href="https://launchdarkly.com/blog/launched-private-user-attributes"
              target="_blank"
              rel="noopener noreferrer"
              className="cta"
            >
              Learn More About Private User Attributes
              <CtaArrow />
            </a>
          </div>
          <div className="column is-2-desktop is-offset-1-desktop is-2-tablet righted">
            <img className="large-icon" src={analytics} alt="analytics" />
          </div>
        </div>
      </div>
    </section>

    <section className="section is-large bg-gradient">
      <div className="container content">
        <div className="section-title">
          <h2 className="title">Data Security Features</h2>
          <p className="subtitle">We built our application with your security concerns in mind.</p>
        </div>
        <div className="columns is-multiline features">
          <div className="column is-half featureList-item columns is-mobile">
            <div className="column is-one-fifth has-text-right-tablet">
              <img src={encrypt} className="icon" alt="encryption" />
            </div>
            <div className="column is-four-fifths">
              <h4>Encryption in Transit</h4>
              <p>With LaunchDarkly your data is safe every step of the way.</p>
            </div>
          </div>
          <div className="column is-half featureList-item columns is-mobile">
            <div className="column is-one-fifth has-text-right-tablet">
              <img src={penTest} className="icon" alt="pen test" />
            </div>
            <div className="column is-four-fifths">
              <h4>Penetration Testing</h4>
              <p>We participate in independent 3rd-party penetration testing every 6 months.</p>
            </div>
          </div>
          <div className="column is-half featureList-item columns is-mobile">
            <div className="column is-one-fifth has-text-right-tablet">
              <img src={mfa} className="icon" alt="mfa" />
            </div>
            <div className="column is-four-fifths">
              <h4>Multi-Factor Authentication</h4>
              <p>Require a second verification step in addition to entering a password.</p>
            </div>
          </div>
          <div className="column is-half featureList-item columns is-mobile">
            <div className="column is-one-fifth has-text-right-tablet">
              <img src={sso} className="icon" alt="sso" />
            </div>
            <div className="column is-four-fifths">
              <h4>SSO with SAML</h4>
              <p>Connect to your existing SAML identity provider to manage team access.</p>
            </div>
          </div>
          <div className="column is-half featureList-item columns is-mobile">
            <div className="column is-one-fifth has-text-right-tablet">
              <img src={auditLog} className="icon" alt="audit log" />
            </div>
            <div className="column is-four-fifths">
              <h4>Audit Logging</h4>
              <p>Get real-time visibility into every feature flag change in your system.</p>
            </div>
          </div>
          <div className="column is-half featureList-item columns is-mobile">
            <div className="column is-one-fifth has-text-right-tablet">
              <img src={custom} className="icon" alt="custom" />
            </div>
            <div className="column is-four-fifths">
              <h4>Custom Roles</h4>
              <p>Create flexible policies that provide fine-grained access control to everything in LaunchDarkly.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
    <ClosingCTAs />
  </Layout>
)

export default Security
